import React, { useCallback, useEffect, useState, } from 'react';
import { connect, } from 'react-redux';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import { useHistory, } from 'react-router-dom';

import Choices from './Choices';
import { Breakpoints, StoreBasicDataApiStructure, } from 'common/types';
import { useWidth, } from 'common/hooks';
import ExpansionPanel from 'common/components/ExpansionPanel';
import Button, { BUTTON_THEME } from 'common/components/Button';
import Icon from 'common/components/Icon';
import { snakeToCamel, } from 'common/functions';
import { useUpdateFilterSearchParams, } from 'common/hooks';
import { repositories, } from 'api';
import { productsAttributesOperations, } from 'store/ducks/products/attributes';
import { productsOperations, } from 'store/ducks/products/collection';
import { isAnySelectedSelector, } from 'store/ducks/products/collection/selectors';
import { ProductsFilterStore, } from 'store/ducks/products/collection/types';
import { pages, } from 'styles';
import { getInfoAboutAllowingRequestBasedOnValidLogin } from 'common/selectors';

type Props = {
    fetchAttributes: Function,
    fetchProducts: Function,
    attributes: StoreBasicDataApiStructure<Array<repositories.products.ProductAttributeType>>,
    productFilters: ProductsFilterStore['filters'],
    isAnySelected: boolean,
    allowRequest: boolean,
};

const collapsingSettingsEnabledOnView: Array<string> = [ Breakpoints.XS, Breakpoints.SM, ];

const Filters: React.FC<Props> = ({
    fetchAttributes,
    attributes,
    fetchProducts,
    productFilters: {
        page,
        limit,
        ...productFilters
    },
    isAnySelected,
    allowRequest,
}) => {
    const history = useHistory();
    const updateFilter = useUpdateFilterSearchParams();
    const currentWidth = useWidth();
    const [ collapse, setCollapse ] = useState<number | null>(null);
    const checkboxClasses = pages.products.makeFilterCheckboxStyles();

    const isMobileView = collapsingSettingsEnabledOnView.includes(currentWidth);

    useEffect(
        () => {
            allowRequest && fetchAttributes();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ allowRequest, ]
    );

    const renderAttributes = useCallback(
        ({ name, code, choices, }: repositories.products.ProductAttributeType, index: number) => {
            const handleCollapse = (panel: number) => (e: React.ChangeEvent, isExpanded: boolean) => {
                isMobileView && setCollapse(isExpanded ? panel : null);
            };

            const preparedCodeProduct = snakeToCamel(code);

            const renderChoices = (choice: repositories.products.ProductAttributeChoiceType) => {
                const handleChange = () => {
                    fetchProducts({ [preparedCodeProduct]: choice.code, page: 1, });

                    const specificProductFilters = productFilters[preparedCodeProduct as keyof typeof productFilters];

                    const filters = {
                        page: 1,
                        limit,
                        ...productFilters,
                        [preparedCodeProduct]: specificProductFilters.includes(choice.code)
                            ? specificProductFilters.filter(code => code !== choice.code)
                            : [ ...specificProductFilters, choice.code ],
                    };

                    updateFilter(filters);
                };

                const checked = productFilters[preparedCodeProduct as keyof typeof productFilters].indexOf(choice.code) > -1;

                return (
                    <Box key={choice.code}
                         mb={0.5}>
                        <FormControlLabel label={choice.name}
                                          control={<Checkbox name={choice.code}
                                                             disableRipple
                                                             classes={checkboxClasses}
                                                             checked={checked}
                                                             onChange={handleChange}/>}/>
                    </Box>
                );
            };

            const renderedChoices = choices.map(renderChoices);

            return isMobileView ? (
                <ExpansionPanel key={code}
                                id={index}
                                noBorder
                                disableTitleMargin
                                expandedPanelId={collapse}
                                handleChange={handleCollapse}
                                header={name}>
                    <Choices name={name}>
                        {renderedChoices}
                    </Choices>
                </ExpansionPanel>
            ) : (
                <Choices key={code}
                         name={name}>
                    {renderedChoices}
                </Choices>
            );
        },
        [ isMobileView, collapse, productFilters, checkboxClasses, fetchProducts, limit, updateFilter, ]
    );

    const removeAllFilters = useCallback(() => {
        fetchProducts();
        history.push({ search: '', });
    }, [ fetchProducts, history, ]);

    return (
        <Box display="flex"
             flexDirection={{ xs: 'column-reverse', md: 'column', }}>
            <Box component="div"
                 display="flex"
                 flexDirection="column">
                {attributes?.data?.map(renderAttributes)}
            </Box>
            {isAnySelected && <Button colorTheme="gray"
                                           theme={BUTTON_THEME.CLEAR_FILTERS}
                                           onClick={removeAllFilters}
                                           startIcon={<Icon.Clear height={20} />}>
                Usuń wszystkie filtry
            </Button>}
        </Box>
    ) ?? null;
};

type MappingType = {
    products: {
        attributes: StoreBasicDataApiStructure<Array<repositories.products.ProductAttributeType>>,
        collection: StoreBasicDataApiStructure<Array<repositories.products.Collection>> & ProductsFilterStore,
    },
    me: StoreBasicDataApiStructure<repositories.MeType>,
};

const mapStateToProps = ({ products, me, }: MappingType) => ({
    allowRequest: getInfoAboutAllowingRequestBasedOnValidLogin(me),
    attributes: products.attributes,
    productFilters: products.collection.filters,
    isAnySelected: isAnySelectedSelector(products),
});

const mapDispatchToProps = {
    fetchAttributes: productsAttributesOperations.request,
    fetchProducts: productsOperations.request,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Filters);
